import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { 
  Users, Briefcase, Crown, CheckCircle2, Shield, 
  Trash2, UserCheck, Settings, Save, Loader2, Euro, Building2, Scale, CreditCard, FileText,
  ExternalLink, Paperclip, Eye, Lightbulb, DraftingCompass, Newspaper
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { HealthCheckPanel } from '../components/admin/HealthCheckPanel';
import { SuggestionsAdminPanel } from '../components/admin/SuggestionsAdminPanel';
import { WeeklyDigestPanel } from '../components/admin/WeeklyDigestPanel';
import { RegistryPanel } from '../components/admin/RegistryPanel';
import { KboOpenDataPanel } from '../components/admin/KboOpenDataPanel';
import { BlogAdminPanel } from '../components/admin/BlogAdminPanel';
import { SeoSettingsPanel } from '../components/admin/SeoSettingsPanel';
import { isJobExpired, jobStatusLabel } from '../lib/jobs';

const AGENT_META = {
  qualification: { label: 'Qualification', color: '#1E3A8A' },
  matching: { label: 'Matching', color: '#10B981' },
  broadcast: { label: 'Alertes chantiers', color: '#0EA5E9' },
  profile_reminder: { label: 'Profil', color: '#F59E0B' },
  review_reminder: { label: 'Avis', color: '#8B5CF6' },
  compliance: { label: 'Conformité', color: '#EF4444' },
  expiry_reminder: { label: 'Expiration annonces', color: '#F97316' },
  pending_apps_reminder: { label: 'Candidatures sans réponse', color: '#EC4899' },
  weekly_digest: { label: 'Rapport hebdo', color: '#6366F1' },
  subscription_sync: { label: 'Sync abonnements', color: '#0891B2' },
  tenders: { label: 'Marchés publics', color: '#7C3AED' },
  health_check: { label: 'Santé du site', color: '#14B8A6' },
  registry_check: { label: 'Contrôle BCE', color: '#B45309' },
  building_zones: { label: 'Zones à construire', color: '#65A30D' },
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [userDetailOpen, setUserDetailOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(false);
  const [agentStats, setAgentStats] = useState(null);

  // Confirmation dialog state
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null, name: '' });

  const getVatVerificationUrl = (vatNumber, country) => {
    if (!vatNumber) return null;
    const cleanVat = vatNumber.replace(/\s/g, '').replace(/\./g, '');
    const c = (country || '').toLowerCase();
    if (c.includes('belg') || cleanVat.startsWith('BE')) {
      const num = cleanVat.replace('BE', '');
      return { url: `https://kbopub.economie.fgov.be/kbopub/toonondernemingps.html?ondernemingsnummer=${num}`, label: 'Vérifier BCE', country: 'BE' };
    } else if (c.includes('franc') || cleanVat.startsWith('FR')) {
      const siren = cleanVat.replace('FR', '').slice(2);
      return { url: `https://www.infogreffe.fr/recherche-siret-siren/resultats?siren=${siren}`, label: 'Vérifier KBIS', country: 'FR' };
    } else if (c.includes('luxem') || cleanVat.startsWith('LU')) {
      const num = cleanVat.replace('LU', '');
      return { url: `https://ecdf.b2g.etat.lu/ecdf/search?q=${num}`, label: 'Vérifier TVA LU', country: 'LU' };
    }
    return { url: `https://ec.europa.eu/taxation_customs/vies/#/vat-validation`, label: 'Vérifier VIES', country: 'EU' };
  };

  const openUserDetail = async (userId) => {
    setLoadingUser(true);
    setUserDetailOpen(true);
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/details`, { withCredentials: true });
      setSelectedUser(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoadingUser(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, jobsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/jobs`, { withCredentials: true }),
        axios.get(`${API}/admin/settings`, { withCredentials: true }).catch(() => ({ data: {} }))
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setJobs(jobsRes.data.jobs || []);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyUser = async (userId, isVerified) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/verify`, { is_verified: !isVerified }, { withCredentials: true });
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) { toast.error('Erreur'); }
  };

  const handleUpdateSubscription = async (userId, plan) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/subscription`, { subscription_plan: plan }, { withCredentials: true });
      toast.success('Abonnement mis à jour');
      fetchData();
    } catch (error) { toast.error('Erreur'); }
  };

  const confirmDeleteUser = (userId, userName) => {
    setDeleteDialog({ open: true, type: 'user', id: userId, name: userName });
  };

  const confirmDeleteJob = (jobId, jobTitle) => {
    setDeleteDialog({ open: true, type: 'job', id: jobId, name: jobTitle });
  };

  const executeDelete = async () => {
    const { type, id } = deleteDialog;
    setDeleteDialog({ open: false, type: null, id: null, name: '' });

    try {
      if (type === 'user') {
        await axios.delete(`${API}/admin/users/${id}`, { withCredentials: true });
        toast.success('Utilisateur supprimé');
      } else if (type === 'job') {
        await axios.delete(`${API}/admin/jobs/${id}`, { withCredentials: true });
        toast.success('Chantier supprimé');
      }
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSettingsChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const [savingPricing, setSavingPricing] = useState(false);
  const PRICING_FIELDS = ['subscription_yearly_price', 'subscription_yearly_label', 'subscription_2years_price', 'subscription_2years_label', 'subscription_monthly_price', 'vat_rate', 'free_jobs_limit', 'free_applications_limit'];
  const fmtEur = (v) => `${(Number(v) || 0).toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  const ttc = (htva) => (Number(htva) || 0) * (1 + (Number(settings.vat_rate) || 0) / 100);

  const handleSavePricing = async () => {
    setSavingPricing(true);
    try {
      const payload = Object.fromEntries(PRICING_FIELDS.filter((k) => settings[k] !== undefined).map((k) => [k, settings[k]]));
      await axios.post(`${API}/admin/settings`, payload, { withCredentials: true });
      toast.success(`Tarifs appliqués : ${fmtEur(settings.subscription_yearly_price)} HTVA / 1 an, ${fmtEur(settings.subscription_2years_price)} HTVA / 2 ans`);
    } catch (error) { toast.error("Erreur lors de l'enregistrement des tarifs"); }
    finally { setSavingPricing(false); }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.post(`${API}/admin/settings`, settings, { withCredentials: true });
      toast.success('Paramètres enregistrés');
    } catch (error) { toast.error('Erreur lors de la sauvegarde'); }
    finally { setSavingSettings(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-heading font-bold text-3xl text-[#111827] mb-2">Administration</h1>
        <p className="text-[#4B5563] mb-4">Gérez la plateforme BuildConnect</p>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-[#111827]">{stats.total_users}</p><p className="text-xs text-[#6B7280]">Utilisateurs</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-[#10B981]">{stats.total_companies}</p><p className="text-xs text-[#6B7280]">Donneurs d'ordre</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-[#F59E0B]">{stats.total_contractors}</p><p className="text-xs text-[#6B7280]">Sous-traitants</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-[#1E3A8A]">{stats.open_jobs}</p><p className="text-xs text-[#6B7280]">Chantiers ouverts</p></CardContent></Card>
          </div>
        )}

        {/* Fix geocoding buttons */}
        <div className="flex gap-3 mb-6">
          <Button variant="outline" size="sm" data-testid="fix-geocoding-jobs" onClick={async () => {
            try {
              toast.info('Géocodage des chantiers en cours...');
              const res = await axios.post(`${API}/jobs/fix-geocoding`, {}, { withCredentials: true });
              toast.success(res.data.message);
            } catch (e) { toast.error('Erreur géocodage chantiers'); }
          }}>
            Corriger géolocalisation chantiers
          </Button>
          <Button variant="outline" size="sm" data-testid="fix-geocoding-users" onClick={async () => {
            try {
              toast.info('Géocodage des utilisateurs en cours...');
              const res = await axios.post(`${API}/admin/fix-geocoding-users`, {}, { withCredentials: true });
              toast.success(res.data.message);
            } catch (e) { toast.error('Erreur géocodage utilisateurs'); }
          }}>
            Corriger géolocalisation utilisateurs
          </Button>
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => { if (!open) setDeleteDialog({ open: false, type: null, id: null, name: '' }); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle data-testid="delete-confirm-title">
                {deleteDialog.type === 'user' ? 'Supprimer cet utilisateur ?' : 'Supprimer ce chantier ?'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {deleteDialog.type === 'user'
                  ? `L'utilisateur "${deleteDialog.name}" sera définitivement supprimé ainsi que tous ses chantiers et candidatures. Cette action est irréversible.`
                  : `Le chantier "${deleteDialog.name}" sera définitivement supprimé ainsi que toutes ses candidatures. Cette action est irréversible.`
                }
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-cancel-btn">Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={executeDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="delete-confirm-btn"
              >
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "users"}>
          <TabsList className="mb-6">
            <TabsTrigger value="users" data-testid="admin-tab-users"><Users className="w-4 h-4 mr-1" /> Utilisateurs</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="admin-tab-jobs"><Briefcase className="w-4 h-4 mr-1" /> Chantiers</TabsTrigger>
            <TabsTrigger value="agents" data-testid="admin-tab-agents"><Shield className="w-4 h-4 mr-1" /> Agents IA</TabsTrigger>
            <TabsTrigger value="suggestions" data-testid="admin-tab-suggestions"><Lightbulb className="w-4 h-4 mr-1" /> Idées</TabsTrigger>
            <TabsTrigger value="prospection" data-testid="admin-tab-prospection"><DraftingCompass className="w-4 h-4 mr-1" /> Prospection</TabsTrigger>
            <TabsTrigger value="blog" data-testid="admin-tab-blog"><Newspaper className="w-4 h-4 mr-1" /> Blog</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings"><Settings className="w-4 h-4 mr-1" /> Paramètres</TabsTrigger>
          </TabsList>

          {/* USERS TAB */}
          <TabsContent value="users">
            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Entreprise</TableHead><TableHead>Email</TableHead>
                      <TableHead className="text-center">Sous-traitant</TableHead><TableHead className="text-center">Donneur d'ordre</TableHead>
                      <TableHead>TVA</TableHead><TableHead>ONSS</TableHead><TableHead>Abonnement</TableHead><TableHead>Statut</TableHead><TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => {
                      const vatInfo = getVatVerificationUrl(u.vat_number, u.country);
                      const isSubcontractor = u.user_type === 'contractor' || u.user_type === 'both';
                      const isCompanyUser = u.user_type === 'company' || u.user_type === 'both';
                      return (
                      <TableRow key={u.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{u.company_name}</p>
                            <p className="text-xs text-[#6B7280]">{u.city}{u.country ? `, ${u.country}` : ''}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{u.email}</TableCell>
                        <TableCell className="text-center">
                          {isSubcontractor ? (
                            <Badge className="bg-[#F59E0B]/10 text-[#92400E]">Oui</Badge>
                          ) : (
                            <span className="text-xs text-[#9CA3AF]">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {isCompanyUser ? (
                            <Badge className="bg-[#10B981]/10 text-[#065F46]">Oui</Badge>
                          ) : (
                            <span className="text-xs text-[#9CA3AF]">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {u.vat_number ? (
                            <div className="flex items-center gap-1">
                              <span className="text-xs font-mono">{u.vat_number}</span>
                              {vatInfo && (
                                <a href={vatInfo.url} target="_blank" rel="noopener noreferrer" title={vatInfo.label}>
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-[#1E3A8A] hover:text-[#1E3A8A]/80" data-testid={`verify-vat-${u.id}`}>
                                    <ExternalLink className="w-3.5 h-3.5" />
                                  </Button>
                                </a>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-[#9CA3AF]">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {u.onss_number ? (
                            <span className="text-xs font-mono">{u.onss_number}</span>
                          ) : (
                            <span className="text-xs text-[#9CA3AF]">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <select className="text-xs border rounded px-2 py-1" value={u.subscription_plan || 'free'} onChange={(e) => handleUpdateSubscription(u.id, e.target.value)}>
                            <option value="free">Gratuit</option><option value="premium">Premium</option>
                          </select>
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => handleVerifyUser(u.id, u.is_verified)} className={u.is_verified ? 'text-emerald-600' : 'text-gray-400'} data-testid={`verify-user-${u.id}`}>
                            {u.is_verified ? <CheckCircle2 className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" className="text-[#1E3A8A] h-8 w-8 p-0" onClick={() => openUserDetail(u.id)} title="Voir documents" data-testid={`view-docs-${u.id}`}>
                              <Paperclip className="w-4 h-4" />
                            </Button>
                            {u.role !== 'admin' && (
                              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0" onClick={() => confirmDeleteUser(u.id, u.company_name)} data-testid={`admin-delete-user-${u.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* User Detail / Documents Modal */}
            <Dialog open={userDetailOpen} onOpenChange={setUserDetailOpen}>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">Détails de l'entreprise</DialogTitle>
                </DialogHeader>
                {loadingUser ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" /></div>
                ) : selectedUser ? (
                  <div className="space-y-6 mt-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div><p className="text-xs text-[#6B7280]">Entreprise</p><p className="font-semibold">{selectedUser.company_name}</p></div>
                      <div><p className="text-xs text-[#6B7280]">Email</p><p className="text-sm">{selectedUser.email}</p></div>
                      <div><p className="text-xs text-[#6B7280]">Téléphone</p><p className="text-sm">{selectedUser.phone || '—'}</p></div>
                      <div><p className="text-xs text-[#6B7280]">Adresse</p><p className="text-sm">{[selectedUser.address, selectedUser.postal_code, selectedUser.city, selectedUser.country].filter(Boolean).join(', ') || '—'}</p></div>
                    </div>
                    <div className="bg-[#F9FAFB] rounded-lg p-4">
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><Building2 className="w-4 h-4 text-[#1E3A8A]" /> Informations légales</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-[#6B7280]">N° de TVA</p>
                          {selectedUser.vat_number ? (
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-mono font-medium">{selectedUser.vat_number}</p>
                              {(() => {
                                const info = getVatVerificationUrl(selectedUser.vat_number, selectedUser.country);
                                return info ? (
                                  <a href={info.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#1E3A8A] hover:underline">
                                    <ExternalLink className="w-3 h-3" />{info.label}
                                  </a>
                                ) : null;
                              })()}
                            </div>
                          ) : (
                            <p className="text-sm text-[#9CA3AF]">Non renseigné</p>
                          )}
                        </div>
                        <div><p className="text-xs text-[#6B7280]">N° ONSS (Belgique)</p><p className="text-sm font-mono">{selectedUser.onss_number || '—'}</p></div>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><Paperclip className="w-4 h-4 text-[#1E3A8A]" /> Documents (Limosa, ONSS, URSSAF...)</h4>
                      {selectedUser.profile_documents && selectedUser.profile_documents.length > 0 ? (
                        <div className="space-y-2">
                          {selectedUser.profile_documents.map((doc) => (
                            <a key={doc.id} href={`${API.replace('/api','')}/api/files/${doc.storage_path}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 bg-[#F9FAFB] rounded border hover:bg-[#F1F5F9] transition-colors">
                              <FileText className="w-4 h-4 text-[#1E3A8A] flex-shrink-0" />
                              <Badge className="bg-[#1E3A8A]/10 text-[#1E3A8A] text-xs">{doc.doc_type}</Badge>
                              <span className="text-sm text-[#374151] truncate flex-1">{doc.original_filename}</span>
                              <span className="text-xs text-[#9CA3AF]">{new Date(doc.uploaded_at).toLocaleDateString('fr-FR')}</span>
                              <Eye className="w-4 h-4 text-[#6B7280]" />
                            </a>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-[#9CA3AF]">Aucun document uploadé</p>
                      )}
                    </div>
                    {selectedUser.detached_workers && selectedUser.detached_workers.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-[#1E3A8A]" /> Personnel détaché</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedUser.detached_workers.map((dw, i) => (
                            <Badge key={`${dw.country}-${dw.count}`} className="bg-[#1E3A8A]/10 text-[#1E3A8A]">{dw.country} : {dw.count} ouvrier{dw.count > 1 ? 's' : ''}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    <div>
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><Shield className="w-4 h-4 text-emerald-500" /> Assurances</h4>
                      {selectedUser.insurance_documents && selectedUser.insurance_documents.length > 0 ? (
                        <div className="space-y-2">
                          {selectedUser.insurance_documents.map((doc) => (
                            <div key={doc.id} className="flex items-center gap-2 p-2 bg-[#F9FAFB] rounded border">
                              <Shield className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{doc.insurance_type || 'Assurance'}</p>
                                <p className="text-xs text-[#6B7280]">Expire: {doc.expiry_date || '—'}</p>
                              </div>
                              {doc.document_url && (
                                <a href={doc.document_url} target="_blank" rel="noopener noreferrer"><Eye className="w-4 h-4 text-[#6B7280]" /></a>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-[#9CA3AF]">Aucune assurance enregistrée</p>
                      )}
                    </div>
                    {selectedUser.portfolio_images && selectedUser.portfolio_images.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-sm mb-3">Portfolio ({selectedUser.portfolio_images.length} photos)</h4>
                        <div className="grid grid-cols-4 gap-2">
                          {selectedUser.portfolio_images.map((img, idx) => (
                            <a key={img} href={img} target="_blank" rel="noopener noreferrer">
                              <img loading="lazy" src={img} alt={`Portfolio ${idx+1}`} className="w-full h-20 object-cover rounded border hover:opacity-80 transition-opacity" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-4 border-t">
                      <div className="flex items-center gap-2">
                        {selectedUser.is_verified ? (
                          <Badge className="bg-emerald-100 text-emerald-700">Vérifié</Badge>
                        ) : (
                          <Badge className="bg-yellow-100 text-yellow-700">Non vérifié</Badge>
                        )}
                        <span className="text-xs text-[#6B7280]">Inscrit le {new Date(selectedUser.created_at).toLocaleDateString('fr-FR')}</span>
                      </div>
                      <Button
                        onClick={() => { handleVerifyUser(selectedUser.id, selectedUser.is_verified); setUserDetailOpen(false); }}
                        className={selectedUser.is_verified ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-emerald-500 hover:bg-emerald-600'}
                        size="sm"
                      >
                        {selectedUser.is_verified ? 'Retirer la vérification' : 'Valider le profil'}
                      </Button>
                    </div>
                  </div>
                ) : null}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* JOBS TAB */}
          <TabsContent value="jobs">
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Titre</TableHead><TableHead>Entreprise</TableHead><TableHead>Ville</TableHead>
                      <TableHead>Budget</TableHead><TableHead>Statut</TableHead><TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {jobs.map((j) => {
                      const isExpired = isJobExpired(j);
                      const st = jobStatusLabel(j);
                      return (
                      <TableRow key={j.id} className={isExpired ? 'opacity-70' : ''}>
                        <TableCell className="font-medium">{j.title}</TableCell>
                        <TableCell className="text-sm">{j.company_name}</TableCell>
                        <TableCell className="text-sm">{j.city}</TableCell>
                        <TableCell className="text-sm">{j.budget_min || j.budget_max ? `${j.budget_min || 0} - ${j.budget_max || 0}€` : 'Sur devis'}</TableCell>
                        <TableCell>
                          <Badge className={st.className}>{st.label}</Badge>
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => confirmDeleteJob(j.id, j.title)} data-testid={`admin-delete-job-${j.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* AGENTS IA TAB */}
          <TabsContent value="agents">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-heading font-bold text-xl">Tableau de bord des Agents IA</h2>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/admin/agents/stats`, { withCredentials: true });
                      setAgentStats(res.data);
                      toast.success('Stats mises à jour');
                    } catch (e) { toast.error('Erreur chargement stats'); }
                  }} data-testid="refresh-agent-stats">
                    Actualiser
                  </Button>
                  <Button size="sm" className="bg-[#1E3A8A]" onClick={async () => {
                    try {
                      await axios.post(`${API}/admin/agents/run`, {}, { withCredentials: true });
                      toast.success('Agents lancés en arrière-plan');
                    } catch (e) { toast.error('Erreur lancement agents'); }
                  }} data-testid="run-agents-btn">
                    Lancer les agents maintenant
                  </Button>
                </div>
              </div>

              {!agentStats ? (
                <Card className="p-8 text-center">
                  <p className="text-[#6B7280] mb-4">Cliquez sur "Actualiser" pour charger les statistiques des agents</p>
                  <Button variant="outline" onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/admin/agents/stats`, { withCredentials: true });
                      setAgentStats(res.data);
                    } catch (e) { toast.error('Erreur'); }
                  }}>Charger les stats</Button>
                </Card>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {Object.entries(AGENT_META).map(([key, agent]) => {
                      const s = agentStats.stats[key] || {};
                      return (
                        <Card key={key} data-testid={`agent-card-${key}`}>
                          <CardContent className="p-4 text-center">
                            <div className="w-3 h-3 rounded-full mx-auto mb-2" style={{backgroundColor: agent.color}} />
                            <p className="text-xs text-[#6B7280] mb-1">{agent.label}</p>
                            <p className="text-2xl font-bold" style={{color: agent.color}}>{s.last_7d || 0}</p>
                            <p className="text-[10px] text-[#9CA3AF]">7 derniers jours</p>
                            <div className="flex justify-between mt-2 text-[10px] text-[#6B7280]">
                              <span>24h: {s.last_24h || 0}</span>
                              <span>30j: {s.last_30d || 0}</span>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>

                  <WeeklyDigestPanel />

                  <RegistryPanel />

                  <HealthCheckPanel />

                  <Card>
                    <CardHeader>
                      <CardTitle className="font-heading text-lg">Activité récente</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0 overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Agent</TableHead><TableHead>Action</TableHead><TableHead>Cible</TableHead><TableHead>Détails</TableHead><TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(agentStats.recent_logs || []).map((log) => (
                            <TableRow key={log.id}>
                              <TableCell>
                                <Badge className="text-xs" style={{
                                  backgroundColor: `${(AGENT_META[log.agent] || { color: '#6B7280' }).color}20`,
                                  color: (AGENT_META[log.agent] || { color: '#6B7280' }).color
                                }}>{(AGENT_META[log.agent] || {}).label || log.agent}</Badge>
                              </TableCell>
                              <TableCell className="text-sm">{log.action}</TableCell>
                              <TableCell className="text-xs text-[#6B7280] font-mono">{log.target_email || log.target_id?.slice(0, 8)}</TableCell>
                              <TableCell className="text-xs text-[#4B5563] max-w-[200px] truncate">{log.details}</TableCell>
                              <TableCell className="text-xs text-[#6B7280]">{new Date(log.created_at).toLocaleString('fr-FR')}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
          </TabsContent>

          <TabsContent value="suggestions">
            <SuggestionsAdminPanel />
          </TabsContent>

          <TabsContent value="prospection">
            <KboOpenDataPanel />
          </TabsContent>

          <TabsContent value="blog">
            <BlogAdminPanel />
          </TabsContent>

          {/* SETTINGS TAB */}
          <TabsContent value="settings">
            {settings && (
              <div className="space-y-6">
                <SeoSettingsPanel settings={settings} onChange={handleSettingsChange} />
                <Card>
                  <CardHeader><CardTitle className="font-heading flex items-center gap-2"><Building2 className="w-5 h-5 text-[#1E3A8A]" /> Informations de l'entreprise</CardTitle></CardHeader>
                  <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><Label>Nom de la plateforme</Label><Input value={settings.platform_name || ''} onChange={(e) => handleSettingsChange('platform_name', e.target.value)} className="input-field" data-testid="setting-platform-name" /></div>
                    <div><Label>Propriétaire (personne physique)</Label><Input value={settings.owner_name || ''} onChange={(e) => handleSettingsChange('owner_name', e.target.value)} className="input-field" data-testid="setting-owner-name" /></div>
                    <div><Label>Exploitant (raison sociale)</Label><Input value={settings.company_legal_name || ''} onChange={(e) => handleSettingsChange('company_legal_name', e.target.value)} className="input-field" data-testid="setting-company-name" /></div>
                    <div><Label>Numéro TVA</Label><Input value={settings.company_vat || ''} onChange={(e) => handleSettingsChange('company_vat', e.target.value)} className="input-field" data-testid="setting-vat" /></div>
                    <div><Label>Adresse du siège</Label><Input value={settings.company_address || ''} onChange={(e) => handleSettingsChange('company_address', e.target.value)} className="input-field" /></div>
                    <div><Label>Code postal</Label><Input value={settings.company_postal_code || ''} onChange={(e) => handleSettingsChange('company_postal_code', e.target.value)} className="input-field" data-testid="setting-postal-code" /></div>
                    <div><Label>Pays</Label><Input value={settings.company_country || ''} onChange={(e) => handleSettingsChange('company_country', e.target.value)} className="input-field" data-testid="setting-country" /></div>
                    <div><Label>Email de contact</Label><Input value={settings.company_email || ''} onChange={(e) => handleSettingsChange('company_email', e.target.value)} className="input-field" /></div>
                    <div><Label>Téléphone</Label><Input value={settings.company_phone || ''} onChange={(e) => handleSettingsChange('company_phone', e.target.value)} className="input-field" /></div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="font-heading flex items-center gap-2"><CreditCard className="w-5 h-5 text-[#1E3A8A]" /> Coordonnées bancaires (virements)</CardTitle></CardHeader>
                  <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><Label>Bénéficiaire</Label><Input value={settings.bank_name || ''} onChange={(e) => handleSettingsChange('bank_name', e.target.value)} className="input-field" data-testid="setting-bank-name" /></div>
                    <div><Label>IBAN</Label><Input value={settings.bank_iban || ''} onChange={(e) => handleSettingsChange('bank_iban', e.target.value)} className="input-field" data-testid="setting-bank-iban" /></div>
                    <div><Label>BIC/SWIFT</Label><Input value={settings.bank_bic || ''} onChange={(e) => handleSettingsChange('bank_bic', e.target.value)} className="input-field" /></div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="font-heading flex items-center gap-2"><Euro className="w-5 h-5 text-[#1E3A8A]" /> Tarifs des abonnements</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 border rounded-lg bg-[#1E3A8A]/5">
                        <Label className="font-semibold">Abonnement 1 an</Label>
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          <div><Label className="text-xs">Libellé</Label><Input value={settings.subscription_yearly_label || ''} onChange={(e) => handleSettingsChange('subscription_yearly_label', e.target.value)} className="input-field" /></div>
                          <div><Label className="text-xs">Prix HTVA</Label><Input type="number" step="0.01" value={settings.subscription_yearly_price || ''} onChange={(e) => handleSettingsChange('subscription_yearly_price', parseFloat(e.target.value) || 0)} className="input-field" data-testid="setting-yearly-price" /></div>
                        </div>
                      </div>
                      <div className="p-4 border rounded-lg bg-[#F59E0B]/5">
                        <Label className="font-semibold">Abonnement 2 ans</Label>
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          <div><Label className="text-xs">Libellé</Label><Input value={settings.subscription_2years_label || ''} onChange={(e) => handleSettingsChange('subscription_2years_label', e.target.value)} className="input-field" /></div>
                          <div><Label className="text-xs">Prix HTVA</Label><Input type="number" step="0.01" value={settings.subscription_2years_price || ''} onChange={(e) => handleSettingsChange('subscription_2years_price', parseFloat(e.target.value) || 0)} className="input-field" data-testid="setting-2years-price" /></div>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div><Label>Prix affiché /mois HTVA</Label><Input type="number" step="0.01" value={settings.subscription_monthly_price ?? ''} onChange={(e) => handleSettingsChange('subscription_monthly_price', parseFloat(e.target.value) || 0)} className="input-field" data-testid="setting-monthly-price" /></div>
                      <div><Label>Taux TVA (%)</Label><Input type="number" step="0.1" value={settings.vat_rate || ''} onChange={(e) => handleSettingsChange('vat_rate', parseFloat(e.target.value) || 0)} className="input-field" data-testid="setting-vat-rate" /></div>
                      <div><Label>Limite chantiers (gratuit)</Label><Input type="number" value={settings.free_jobs_limit ?? ''} onChange={(e) => handleSettingsChange('free_jobs_limit', parseInt(e.target.value) || 0)} className="input-field" /></div>
                      <div><Label>Limite candidatures (gratuit, 0 = illimité)</Label><Input type="number" value={settings.free_applications_limit ?? ''} onChange={(e) => handleSettingsChange('free_applications_limit', parseInt(e.target.value) || 0)} className="input-field" /></div>
                    </div>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-4 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB]" data-testid="pricing-preview">
                      <div className="text-sm text-[#4B5563]">
                        <p className="font-semibold text-[#111827] mb-1">Aperçu côté utilisateur (Belgique, TVA {settings.vat_rate || 0}%)</p>
                        <p>{fmtEur(settings.subscription_monthly_price)}/mois HTVA · <strong>{fmtEur(ttc(settings.subscription_yearly_price))} TTC</strong> pour 1 an ({fmtEur(settings.subscription_yearly_price)} HTVA) · <strong>{fmtEur(ttc(settings.subscription_2years_price))} TTC</strong> pour 2 ans ({fmtEur(settings.subscription_2years_price)} HTVA)</p>
                        <p className="text-xs text-[#6B7280] mt-1">Appliqué immédiatement à la page Abonnement et aux paiements Stripe.</p>
                      </div>
                      <Button className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 flex-shrink-0" onClick={handleSavePricing} disabled={savingPricing} data-testid="save-pricing-btn">
                        {savingPricing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} OK — Appliquer les tarifs
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="font-heading flex items-center gap-2"><Scale className="w-5 h-5 text-[#1E3A8A]" /> Informations légales</CardTitle></CardHeader>
                  <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><Label>Droit applicable</Label><Input value={settings.applicable_law || ''} onChange={(e) => handleSettingsChange('applicable_law', e.target.value)} className="input-field" /></div>
                    <div><Label>Juridiction compétente</Label><Input value={settings.legal_jurisdiction || ''} onChange={(e) => handleSettingsChange('legal_jurisdiction', e.target.value)} className="input-field" /></div>
                    <div><Label>Email DPO (RGPD)</Label><Input value={settings.dpo_email || ''} onChange={(e) => handleSettingsChange('dpo_email', e.target.value)} className="input-field" /></div>
                    <div><Label>Email protection des données</Label><Input value={settings.privacy_email || ''} onChange={(e) => handleSettingsChange('privacy_email', e.target.value)} className="input-field" /></div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="font-heading flex items-center gap-2"><FileText className="w-5 h-5 text-[#1E3A8A]" /> Contenu des pages légales</CardTitle></CardHeader>
                  <CardContent className="space-y-6">
                    <p className="text-sm text-[#6B7280]">Personnalisez le contenu des pages légales. Si un champ est vide, le contenu par défaut sera affiché.</p>
                    <div>
                      <Label className="text-base font-semibold mb-2 block">Conditions Générales d'Utilisation</Label>
                      <Textarea value={settings.terms_content || ''} onChange={(e) => handleSettingsChange('terms_content', e.target.value)} className="input-field min-h-[200px] font-mono text-sm" placeholder="Laissez vide pour utiliser le contenu par défaut." data-testid="setting-terms-content" />
                    </div>
                    <div>
                      <Label className="text-base font-semibold mb-2 block">Politique de Confidentialité</Label>
                      <Textarea value={settings.privacy_content || ''} onChange={(e) => handleSettingsChange('privacy_content', e.target.value)} className="input-field min-h-[200px] font-mono text-sm" placeholder="Laissez vide pour utiliser le contenu par défaut." data-testid="setting-privacy-content" />
                    </div>
                    <div>
                      <Label className="text-base font-semibold mb-2 block">Mentions Légales</Label>
                      <Textarea value={settings.legal_notice_content || ''} onChange={(e) => handleSettingsChange('legal_notice_content', e.target.value)} className="input-field min-h-[200px] font-mono text-sm" placeholder="Laissez vide pour utiliser le contenu par défaut." data-testid="setting-legal-notice-content" />
                    </div>
                  </CardContent>
                </Card>
                <Button className="btn-primary w-full text-lg py-5 gap-2" onClick={handleSaveSettings} disabled={savingSettings} data-testid="save-settings-btn">
                  {savingSettings ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                  {savingSettings ? 'Enregistrement...' : 'Enregistrer tous les paramètres'}
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
